import { useContext, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowUpRight,
  CircleDot,
  Map,
  Medal,
  Plus,
  ShieldCheck,
  Trophy,
  Users,
} from 'lucide-react';
import { collection, doc, getCountFromServer, setDoc } from 'firebase/firestore';
import { toast } from 'sonner';
import { db } from '../../firebase/config';
import { AuthContext } from '../../contexts/AuthContext';
import { useSiteConfig } from '../../contexts/SiteConfigContext';
import { useTournament } from '../../contexts/TournamentContext';
import { PERMISSIONS } from '../../constants/permissions';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { cn } from '../../lib/utils';
import PageHeader from './shell/PageHeader';
import GrowthChart from './GrowthChart';
import { FORMAT_LABELS } from './shell/tournamentTools';

const toDate = (v) => (v && typeof v.toDate === 'function' ? v.toDate() : v ? new Date(v) : null);

const formatDateRange = (start, end) => {
  const fmt = (d) =>
    d
      ? d
          .toLocaleDateString('en-US', { month: 'short', day: '2-digit' })
          .toUpperCase()
          .replace(/\./g, '')
      : '—';
  return `${fmt(start)} → ${fmt(end)}`;
};

const statusStyles = {
  active: 'border-heat/60 text-heat',
  upcoming: 'border-border text-foreground',
  completed: 'border-border/60 text-muted-foreground',
};

const Stat = ({ label, value, sub, accent }) => (
  <Card className="border-border/60 bg-background/40">
    <CardContent className="pt-6">
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
        {label}
      </div>
      <div
        className={cn(
          'mt-3 font-display text-5xl font-bold leading-none tabular-nums',
          accent && 'text-heat'
        )}
      >
        {value}
      </div>
      {sub && (
        <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
          {sub}
        </div>
      )}
    </CardContent>
  </Card>
);

const QuickAction = ({ to, icon: Icon, label, disabled, hint }) => {
  const content = (
    <span className="flex items-center justify-between gap-3">
      <span className="flex items-center gap-3">
        {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
        <span className="text-sm font-medium">{label}</span>
      </span>
      <span className="flex items-center gap-2">
        {hint && (
          <span className="font-mono text-[9px] uppercase tracking-wider text-muted-foreground">
            {hint}
          </span>
        )}
        <ArrowUpRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </span>
    </span>
  );

  const baseClass =
    'group relative block border border-border/60 px-4 py-4 transition-colors hover:border-foreground/40 hover:bg-foreground/[0.03]';

  if (disabled) {
    return (
      <span className={cn(baseClass, 'cursor-not-allowed opacity-50')} aria-disabled>
        {content}
      </span>
    );
  }
  return (
    <Link to={to} className={baseClass}>
      {content}
    </Link>
  );
};

const Dashboard = () => {
  const { userProfile, isSuperAdmin, hasPermission } = useContext(AuthContext);
  const { config, loading: siteConfigLoading } = useSiteConfig();
  const { tournaments, selected, selectedId, loading: tournamentsLoading } = useTournament();
  const [playerCount, setPlayerCount] = useState(null);
  const [savingCotw, setSavingCotw] = useState(false);

  const setCotwPublished = async (published) => {
    setSavingCotw(true);
    try {
      await setDoc(
        doc(db, 'siteConfig', 'main'),
        { compOfTheWeek: { published } },
        { merge: true }
      );
      toast.success(published ? 'Comp of the Week published.' : 'Comp of the Week unpublished.');
    } catch (error) {
      toast.error('Could not update Comp of the Week.', { description: error.message });
    } finally {
      setSavingCotw(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const snap = await getCountFromServer(collection(db, 'userProfiles'));
        if (!cancelled) setPlayerCount(snap.data().count);
      } catch {
        // Permission or network error — show em-dash.
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, []);

  const stats = useMemo(() => {
    const active = tournaments.filter((t) => t.computedStatus === 'active').length;
    const upcoming = tournaments.filter((t) => t.computedStatus === 'upcoming').length;
    const completed = tournaments.filter((t) => t.computedStatus === 'completed').length;
    return { active, upcoming, completed, total: tournaments.length };
  }, [tournaments]);

  const recent = useMemo(() => {
    return [...tournaments]
      .sort((a, b) => {
        const rank = { active: 0, upcoming: 1, completed: 2 };
        const r = (rank[a.computedStatus] ?? 3) - (rank[b.computedStatus] ?? 3);
        if (r !== 0) return r;
        return (toDate(b.startDate) || 0) - (toDate(a.startDate) || 0);
      })
      .slice(0, 6);
  }, [tournaments]);

  const superAdmin = isSuperAdmin();

  const tournamentFormatLabel = selected ? FORMAT_LABELS[selected.format || 'leaderboard'] : null;

  return (
    <div className="pb-16">
      <PageHeader
        eyebrow="Overview"
        title={superAdmin ? 'Super admin dashboard' : 'Tournament admin dashboard'}
        description={
          userProfile?.displayName
            ? `Welcome back, ${userProfile.displayName}.`
            : 'Manage tournaments, players, and content.'
        }
        actions={
          superAdmin ? (
            <Button asChild>
              <Link to="/admin/tournaments">
                <Plus className="h-4 w-4" />
                New tournament
              </Link>
            </Button>
          ) : null
        }
      />

      <section className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <Stat
          label="Live now"
          value={stats.active}
          accent={stats.active > 0}
          sub={stats.active === 1 ? '1 tournament running' : `${stats.active} tournaments running`}
        />
        <Stat label="Upcoming" value={stats.upcoming} sub="on the calendar" />
        <Stat label="Completed" value={stats.completed} sub="all seasons" />
        <Stat
          label="Players"
          value={playerCount ?? '—'}
          sub={playerCount === null ? 'read restricted' : 'registered profiles'}
        />
      </section>

      <GrowthChart tournaments={tournaments} />

      {hasPermission(PERMISSIONS.CONTENT_EDIT) && (
        <Card className="mt-6 border-border/60 bg-background/40">
          <CardContent className="flex flex-wrap items-center justify-between gap-4 pt-6">
            <div>
              <div className="font-display text-lg font-semibold">Comp of the Week</div>
              <p className="mt-1 text-sm text-muted-foreground">
                Publish the page and show its link in the desktop and mobile navigation.
              </p>
            </div>
            <label className="flex items-center gap-3 text-sm">
              <span>{config.compOfTheWeek.published ? 'Published' : 'Unpublished'}</span>
              <Switch
                aria-label="Publish Comp of the Week"
                checked={config.compOfTheWeek.published}
                disabled={siteConfigLoading || savingCotw}
                onCheckedChange={setCotwPublished}
              />
            </label>
          </CardContent>
        </Card>
      )}

      <section className="mt-12 grid gap-6 lg:grid-cols-[1.4fr_1fr]">
        <Card className="border-border/60 bg-background/40">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
                Selected tournament
              </div>
              <CardTitle className="mt-1 font-display text-2xl">
                {selected ? selected.name : 'No tournament selected'}
              </CardTitle>
            </div>
            {selected && (
              <Badge
                variant={selected.computedStatus === 'active' ? 'live' : 'outline'}
                className="uppercase"
              >
                {selected.computedStatus === 'active' && <CircleDot className="h-2.5 w-2.5" />}
                {selected.computedStatus}
              </Badge>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            {selected ? (
              <>
                <div className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground tabular-nums">
                  {formatDateRange(toDate(selected.startDate), toDate(selected.endDate))} ·{' '}
                  {tournamentFormatLabel}
                </div>
                <QuickAction
                  to={`/admin/tournaments/${selected.id}`}
                  icon={Trophy}
                  label="Open tournament admin"
                />
                <p className="text-xs text-muted-foreground">
                  Setup, format, and results are all on the tournament admin page.
                </p>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">
                Pick a tournament in the top bar to manage its stages, players and results.
              </p>
            )}
          </CardContent>
        </Card>

        <Card className="border-border/60 bg-background/40">
          <CardHeader>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
              Quick actions
            </div>
            <CardTitle className="mt-1 font-display text-2xl">Jump in</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {superAdmin ? (
              <>
                <QuickAction to="/admin/tournaments" label="Manage tournaments" icon={Trophy} />
                <QuickAction to="/admin/teams" label="Manage teams" icon={Users} />
                <QuickAction to="/admin/media" label="Media library" icon={Map} />
                <QuickAction to="/admin/map-images" label="Map image library" icon={Map} />
                <QuickAction to="/admin/users" label="Users & permissions" icon={ShieldCheck} />
                <QuickAction
                  to="/admin/tournament-winners"
                  label="Tournament winners"
                  icon={Medal}
                />
              </>
            ) : (
              <>
                {selectedId ? (
                  <QuickAction
                    to={`/admin/tournaments/${selectedId}`}
                    label="Open tournament admin"
                    icon={Trophy}
                  />
                ) : (
                  <QuickAction to="/admin/tournaments" label="Pick a tournament" icon={Trophy} />
                )}
                <QuickAction to="/admin/map-images" label="Map image library" icon={Map} />
              </>
            )}
          </CardContent>
        </Card>
      </section>

      <section className="mt-12">
        <div className="mb-6 flex items-end justify-between">
          <div>
            <div className="mb-3 flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
              <span className="h-px w-6 bg-heat" />
              Tournaments
            </div>
            <h2 className="font-display text-3xl font-bold">Recent &amp; upcoming</h2>
          </div>
          <Link
            to="/admin/tournaments"
            className="hidden items-center gap-1.5 font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground transition-colors hover:text-foreground md:inline-flex"
          >
            See all
            <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>
        </div>

        {tournamentsLoading ? (
          <div className="divide-y divide-border/60 border-y border-border/60">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="flex h-16 animate-pulse items-center gap-6 px-2">
                <div className="h-3 w-6 bg-muted" />
                <div className="h-4 w-40 bg-muted" />
                <div className="ml-auto h-3 w-24 bg-muted" />
              </div>
            ))}
          </div>
        ) : recent.length === 0 ? (
          <div className="border border-dashed border-border/60 px-6 py-12 text-center text-sm text-muted-foreground">
            No tournaments yet. {superAdmin && 'Create your first one above.'}
          </div>
        ) : (
          <ol className="divide-y divide-border/60 border-y border-border/60">
            {recent.map((t, i) => (
              <li key={t.id} className="group">
                <Link
                  to={`/admin/tournaments/${t.id}`}
                  className="grid grid-cols-[auto_1fr_auto] items-center gap-4 px-2 py-4 transition-colors hover:bg-foreground/[0.03] sm:grid-cols-[auto_1fr_auto_auto_auto] sm:gap-8"
                >
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <div className="min-w-0">
                    <div className="truncate font-display text-lg font-semibold tracking-tight sm:text-xl">
                      {t.name}
                    </div>
                    <div className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                      {(FORMAT_LABELS[t.format] || t.format || 'event').toString().toUpperCase()}
                    </div>
                  </div>
                  <div className="hidden font-mono text-xs uppercase tracking-[0.2em] text-foreground/70 tabular-nums sm:block">
                    {formatDateRange(toDate(t.startDate), toDate(t.endDate))}
                  </div>
                  <span
                    className={cn(
                      'hidden border px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.2em] sm:inline-flex',
                      statusStyles[t.computedStatus] || statusStyles.upcoming
                    )}
                  >
                    {t.computedStatus}
                  </span>
                  <ArrowUpRight className="h-4 w-4 text-muted-foreground transition-all group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-foreground" />
                </Link>
              </li>
            ))}
          </ol>
        )}
      </section>
    </div>
  );
};

export default Dashboard;
